import { Source } from '../types';

export interface MediaItem {
  url: string;  // For PDFs with thumbnails, this is the thumbnail URL
  title: string;
  filename?: string;
  source_type?: string;
  confidence_score?: number;
  type: 'image' | 'pdf' | 'document';
  fileExtension?: string;
  documentUrl?: string;  // For PDFs, this is the actual PDF URL
}

// Keep ImageItem for backward compatibility
export interface ImageItem extends MediaItem {
  type: 'image';
}

/**
 * Common file extensions by type
 */
const IMAGE_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.tiff', '.ico'
];

const PDF_EXTENSIONS = ['.pdf'];

const DOCUMENT_EXTENSIONS = [
  '.doc', '.docx', '.txt', '.pptx', '.ppt', '.xls', '.xlsx'
];

/**
 * Check if a URL or filename represents an image file
 */
export const isImageFile = (urlOrFilename: string): boolean => {
  if (!urlOrFilename) return false;
  
  const lowerUrl = urlOrFilename.toLowerCase();
  
  // Check for standard image file extensions
  const hasExtension = IMAGE_EXTENSIONS.some(ext => lowerUrl.includes(ext));
  if (hasExtension) {
    return true;
  }
  
  // Check for known image CDN domains (Widen, etc.)
  const imageCdnPatterns = [
    'widencdn.net',
    'widencollective.com/preview',
    '/thumbnail/',
    '/preview/',
    'cloudinary.com',
    'amazonaws.com'
  ];
  
  const hasCdnPattern = imageCdnPatterns.some(pattern => lowerUrl.includes(pattern));
  return hasCdnPattern;
};

/**
 * Check if a URL or filename represents a PDF file
 */
export const isPDFFile = (urlOrFilename: string): boolean => {
  if (!urlOrFilename) return false;
  
  const lowerUrl = urlOrFilename.toLowerCase();
  return PDF_EXTENSIONS.some(ext => lowerUrl.includes(ext));
};

/**
 * Check if a URL or filename represents a document file
 */
export const isDocumentFile = (urlOrFilename: string): boolean => {
  if (!urlOrFilename) return false;
  
  const lowerUrl = urlOrFilename.toLowerCase();
  return DOCUMENT_EXTENSIONS.some(ext => lowerUrl.includes(ext));
};

/**
 * Determine the file type and extension
 */
export const getFileType = (urlOrFilename: string): { type: 'image' | 'pdf' | 'document' | 'unknown', extension: string } => {
  if (!urlOrFilename) return { type: 'unknown', extension: '' };
  
  const extension = urlOrFilename.toLowerCase().split('.').pop() || '';
  
  if (isImageFile(urlOrFilename)) {
    return { type: 'image', extension };
  } else if (isPDFFile(urlOrFilename)) {
    return { type: 'pdf', extension };
  } else if (isDocumentFile(urlOrFilename)) {
    return { type: 'document', extension };
  }
  
  return { type: 'unknown', extension };
};

/**
 * Extract all media files (images and PDFs) from chat sources
 */
export const extractMediaFromSources = (sources: Source[]): MediaItem[] => {
  if (!sources || !Array.isArray(sources)) {
    return [];
  }

  const mediaItems: MediaItem[] = [];

  sources.forEach(source => {
    const docFileType = getFileType(source.document_url || '');
    const isPdf = docFileType.type === 'pdf';

    // FranConnect documents now use a stable resolver URL
    // (.../api/v1/documents/{id}/download) that doesn't carry a .pdf extension,
    // so detect them via source_type + presence of a thumbnail.
    const isFranconnectWithThumb =
      source.source_type === 'franconnect' && !!source.metadata?.thumbnail_url;

    // For PDFs (or FranConnect docs) with thumbnails, render the thumbnail
    // as the preview image and link through to the actual document URL.
    if ((isPdf || isFranconnectWithThumb) && source.metadata?.thumbnail_url) {
      mediaItems.push({
        url: source.metadata.thumbnail_url,
        title: source.title || 'Untitled PDF',
        filename: extractFilenameFromUrl(source.document_url || ''),
        source_type: source.source_type,
        confidence_score: source.confidence_score,
        type: 'pdf',
        fileExtension: '.pdf',
        documentUrl: source.document_url,
      });
      return;
    }
    
    // Check if the source has a document_url that's a media file
    if (source.document_url) {
      const fileType = getFileType(source.document_url);
      
      if (fileType.type === 'image' || fileType.type === 'pdf') {
        mediaItems.push({
          url: source.document_url,
          title: source.title || 'Untitled File',
          filename: extractFilenameFromUrl(source.document_url),
          source_type: source.source_type,
          confidence_score: source.confidence_score,
          type: fileType.type,
          fileExtension: fileType.extension,
        });
      }
    }
    
    // Also check metadata for additional media URLs
    if (source.metadata) {
      const metadataUrls = [
        { url: source.metadata.thumbnail_url, suffix: '(Thumbnail)' },
        { url: source.metadata.preview_url, suffix: '(Preview)' },
        { url: source.metadata.public_url, suffix: '' }
      ];

      metadataUrls.forEach(({ url, suffix }) => {
        if (url && url !== source.document_url) {
          const fileType = getFileType(url);
          
          if (fileType.type === 'image' || fileType.type === 'pdf') {
            // Check if this URL is already added
            const isDuplicate = mediaItems.some(item => item.url === url);
            
            if (!isDuplicate) {
              mediaItems.push({
                url,
                title: `${source.title || 'Untitled'} ${suffix}`.trim(),
                filename: extractFilenameFromUrl(url),
                source_type: source.source_type,
                confidence_score: source.confidence_score,
                type: fileType.type,
                fileExtension: fileType.extension,
              });
            }
          }
        }
      });
    }
  });

  // Remove duplicates based on URL
  const uniqueMedia = mediaItems.filter((item, index, self) => 
    index === self.findIndex(media => media.url === item.url)
  );

  // Sort by confidence score (highest first)
  return uniqueMedia.sort((a, b) => (b.confidence_score || 0) - (a.confidence_score || 0));
};

/**
 * Extract image URLs from chat sources (backward compatibility)
 */
export const extractImagesFromSources = (sources: Source[]): ImageItem[] => {
  const mediaItems = extractMediaFromSources(sources);
  const imageItems = mediaItems.filter(item => item.type === 'image') as ImageItem[];
  
  // Debug logging - v3
  console.log('🖼️ WIDEN THUMBNAIL DEBUG v3:', {
    totalSources: sources.length,
    totalMediaItems: mediaItems.length,
    imageItems: imageItems.length,
    imageUrls: imageItems.map(item => item.url.substring(0, 50) + '...'),
    firstImageUrl: imageItems.length > 0 ? imageItems[0].url : 'none'
  });
  
  return imageItems;
};

/**
 * Extract filename from URL
 */
const extractFilenameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const filename = pathname.split('/').pop() || 'image';
    
    // Decode URI component to handle encoded characters
    return decodeURIComponent(filename);
  } catch {
    // If URL parsing fails, try simple extraction
    const parts = url.split('/');
    return parts[parts.length - 1] || 'image';
  }
};

/**
 * Check if sources contain any media files (images or PDFs)
 */
export const hasMedia = (sources: Source[]): boolean => {
  return extractMediaFromSources(sources).length > 0;
};

/**
 * Check if sources contain any images (backward compatibility)
 */
export const hasImages = (sources: Source[]): boolean => {
  return extractImagesFromSources(sources).length > 0;
};

/**
 * Get media count from sources
 */
export const getMediaCount = (sources: Source[]): number => {
  return extractMediaFromSources(sources).length;
};

/**
 * Get image count from sources (backward compatibility)
 */
export const getImageCount = (sources: Source[]): number => {
  return extractImagesFromSources(sources).length;
};
